import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SectionCard } from "../components/SectionCard";
import { ToggleRow } from "../components/ToggleRow";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { api, notify } from "../lib/api";

export function AdminTournamentFormPage() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { setTrail } = useBreadcrumbs();
  const isEdit = Boolean(tournamentId);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [quotaHands, setQuotaHands] = useState(30);
  const [topN, setTopN] = useState(4);
  const [supportBlankLimit, setSupportBlankLimit] = useState(10);
  const [beloteEnabled, setBeloteEnabled] = useState(true);
  const [sansAtoutEnabled, setSansAtoutEnabled] = useState(true);
  const [toutAtoutEnabled, setToutAtoutEnabled] = useState(true);
  const [surcoincheEnabled, setSurcoincheEnabled] = useState(true);
  const [blankGamesEnabled, setBlankGamesEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    let active = true;
    setTrail([]);
    if (!tournamentId) {
      setTrail([
        { label: "Tournois", to: "/tournaments" },
        { label: "Nouveau tournoi" },
      ]);
      return;
    }
    void api.getAdminTournament(tournamentId).then((data) => {
      if (!active) return;
      const t = data.tournament;
      setName(t.name);
      setDescription(t.description);
      setQuotaHands(t.quotaHands);
      setTopN(t.topN);
      setSupportBlankLimit(t.supportBlankLimit);
      setBeloteEnabled(t.ruleset.beloteEnabled);
      setSansAtoutEnabled(t.ruleset.sansAtoutEnabled);
      setToutAtoutEnabled(t.ruleset.toutAtoutEnabled);
      setSurcoincheEnabled(t.ruleset.surcoincheEnabled);
      setBlankGamesEnabled(t.ruleset.blankGamesEnabled);
      setTrail([
        { label: "Tournois", to: "/tournaments" },
        { label: t.name, to: `/tournaments/${tournamentId}` },
        { label: "Modifier" },
      ]);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [tournamentId, setTrail]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      name,
      description,
      quotaHands,
      topN,
      supportBlankLimit,
      finalsFormat: "top_4",
      ruleset: {
        beloteEnabled,
        sansAtoutEnabled,
        toutAtoutEnabled,
        surcoincheEnabled,
        foulEnabled: true,
        blankGamesEnabled,
      },
    };

    const action = isEdit
      ? api.updateTournament(tournamentId!, payload)
      : api.createTournament(payload);

    void action
      .then((result) => {
        notify("success", isEdit ? "Tournoi mis à jour" : "Tournoi créé");
        navigate(`/admin/tournaments/${result.tournament.id}`);
      })
      .catch(() => {})
      .finally(() => setSubmitting(false));
  }

  if (loading) {
    return (
      <div className="sm:max-w-lg animate-pulse">
        <div className="mb-5">
          <div className="h-7 w-48 rounded bg-gray-200" />
        </div>
        <div className="space-y-5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card">
              <div className="h-4 w-24 rounded bg-gray-200" />
              <div className="mt-3 space-y-3">
                <div className="h-10 rounded bg-gray-100" />
                <div className="h-10 rounded bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="sm:max-w-lg">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-ink">
          {isEdit ? "Modifier le tournoi" : "Nouveau tournoi"}
        </h1>
      </div>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <SectionCard title="Informations">
          <div className="space-y-4">
            <div>
              <label className="label" htmlFor="tournament-name">
                Nom
              </label>
              <input
                id="tournament-name"
                type="text"
                className="text-field"
                required
                minLength={2}
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="tournament-desc">
                Description
              </label>
              <textarea
                id="tournament-desc"
                className="text-field"
                rows={3}
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Configuration">
          <div className="space-y-4">
            <div>
              <label className="label" htmlFor="quota-hands">
                Quota de levées par équipe
              </label>
              <input
                id="quota-hands"
                type="number"
                className="text-field"
                min={1}
                max={200}
                value={quotaHands}
                onChange={(e) => setQuotaHands(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="label" htmlFor="top-n">
                Top N qualifiés
              </label>
              <input
                id="top-n"
                type="number"
                className="text-field"
                min={2}
                max={16}
                value={topN}
                onChange={(e) => setTopN(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="label" htmlFor="blank-limit">
                Limite levées à blanc (support)
              </label>
              <input
                id="blank-limit"
                type="number"
                className="text-field"
                min={0}
                max={50}
                value={supportBlankLimit}
                onChange={(e) => setSupportBlankLimit(Number(e.target.value))}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Règles">
          <div className="space-y-2">
            <ToggleRow
              id="belote"
              label="Belote"
              checked={beloteEnabled}
              onChange={setBeloteEnabled}
            />
            <ToggleRow
              id="sans-atout"
              label="Sans atout"
              checked={sansAtoutEnabled}
              onChange={setSansAtoutEnabled}
            />
            <ToggleRow
              id="tout-atout"
              label="Tout atout"
              checked={toutAtoutEnabled}
              onChange={setToutAtoutEnabled}
            />
            <ToggleRow
              id="surcoinche"
              label="Surcoinche"
              checked={surcoincheEnabled}
              onChange={setSurcoincheEnabled}
            />
            <ToggleRow
              id="blank-games"
              label="Parties à blanc"
              checked={blankGamesEnabled}
              onChange={setBlankGamesEnabled}
            />
          </div>
        </SectionCard>

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting
              ? "Enregistrement..."
              : isEdit
                ? "Enregistrer"
                : "Créer le tournoi"}
          </button>
        </div>
      </form>
    </div>
  );
}
